import { useMemo } from 'react';

import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { usePlaceholderContent } from '@sitecore/common';
import { OfferStepRendering } from '@sitecore/types/OfferStep';

import {
  useConsumptionOverview,
  useOfferFetching,
  useOfferFiltering,
  useOfferForm,
  type FormFields,
  useOfferLayout,
  useOfferTracking,
} from './hooks';
import { FlowConsumerContextEnergy } from '../../../context';
import { useCachedOfferMachine } from '../../../shared/hooks';
import { Offer, OfferResponse } from '../../../shared/types';

/** Same rule as `getProducts`: Time of Use stays for a double meter, and for contract extensions. */
const isPromotedTimeOfUseUnavailable = (
  offer: OfferResponse | undefined,
  discountCode: string,
  hasDoubleMeter: boolean | undefined,
  customerId: number | null | undefined,
): boolean => {
  if (customerId || hasDoubleMeter) return false;

  const combination = offer?.productCombinations?.find(item => item.discountCode === discountCode);

  return !!combination?.products?.some(product => {
    const selectedProduct = offer?.products?.find(item => item.marketingProductCode === product.marketingProductCode);

    return selectedProduct?.priceDifferentiation === 'TimeOfUse';
  });
};

export const getPromotedOffer = ({
  offer,
  offers,
  allOffers,
  hasDoubleMeter,
  customerId,
}: {
  offer?: OfferResponse;
  offers?: Offer[];
  allOffers?: Offer[];
  hasDoubleMeter?: boolean;
  customerId?: number | null;
}): Offer | undefined => {
  const promotion = offer?.promotion;
  if (promotion?.status !== 'Promoted' || !promotion.discountCode) return undefined;

  if (isPromotedTimeOfUseUnavailable(offer, promotion.discountCode, hasDoubleMeter, customerId)) {
    return undefined;
  }

  const candidates = allOffers && allOffers.length > 0 ? allOffers : (offers ?? []);

  return candidates.find(item => item.type === promotion.discountCode);
};

export interface UseOfferStepLogicOptions {
  /** Override useGetOffers params. Omit to use the default TOU experiment filter. Set to `null` to skip experiment filtering entirely and use getOffers() directly. */
  getOffersConfig?: {
    experimentKey: string;
    filterPredicate: (offer: Offer, variant: string | null) => boolean;
  } | null;

  /** Post-process offers after filterOffers(). E.g., strip TOU offers from the main list. */
  transformOffers?: (filteredOffers: Offer[], allFilteredOffers: Offer[]) => Offer[];

  /** Whether to pre-select the discount code on SUCCESS. Default: true. */
  isPreSelectionEnabled?: boolean;

  /** Called on mount with sendToFlowMachine, for additional side effects. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMount?: (sendToFlowMachine: (event: any) => void) => void;

  /** Custom scroll behavior for product filter changes. */
  scrollBehavior?: 'layout-effect' | 'effect';

  /** Custom scroll block position. Default: 'center'. */
  scrollBlock?: ScrollLogicalPosition;

  /** Additional callback when productFilter changes. */
  onProductFilterChange?: () => void;

  /** Custom onValueChange for the RadioGroup (card selection). */
  onRadioValueChange?: (value: string, defaultOnChange: (value: string) => void) => void;

  /** Custom payload builder for the submit effect. Return values to merge into the navigation payload. */
  buildSubmitPayload?: (args: {
    getValues: () => FormFields;
    selectedDiscountCode?: string;
    flowContext: FlowConsumerContextEnergy;
    offer: ReturnType<typeof useCachedOfferMachine>['offerState']['context']['offer'];
    allOffers: Offer[];
  }) => Record<string, unknown>;

  /** Event type to send to the offer machine on mount. Default: 'GET_OFFER'. */
  offerMachineEventType?: 'GET_OFFER' | 'GET_EXTENSION_OFFER';

  /** Extra payload fields to send with the offer machine event on mount. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offerMachinePayload?: Record<string, any>;

  /** Override the showProductToggle condition. Receives the default computed value. */
  showProductToggleOverride?: (defaultValue: boolean) => boolean;

  /** Custom form validation message for the discountCode field. */
  validationMessage?: string;

  /** Auto-reselect discount code when available offers change and current selection is no longer valid. */
  autoReselectOnOffersChange?: boolean;

  /** Extract Sitecore params from the placeholder content. Return value is merged into the hook scope. */
  extractParams?: (rendering?: OfferStepRendering) => Record<string, unknown>;
}

export function useOfferStepLogic(options: UseOfferStepLogicOptions = {}) {
  const {
    getOffersConfig,
    transformOffers,
    isPreSelectionEnabled = true,
    onMount,
    scrollBehavior = 'layout-effect',
    scrollBlock = 'center',
    onProductFilterChange,
    onRadioValueChange,
    buildSubmitPayload,
    offerMachineEventType = 'GET_OFFER',
    offerMachinePayload,
    showProductToggleOverride,
    validationMessage = 'Selecteer het contracttype dat bij jou past om verder te gaan.',
    autoReselectOnOffersChange = false,
  } = options;

  // --- Shared context ---
  const { businessUnit, label } = useDC();
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const sendToFlowMachine = useFlowActorRef().send;
  const { context: flowContext } = flowState;

  const placeholderContent = usePlaceholderContent<{ OfferStep: OfferStepRendering }>();
  const { OfferStep: { fields: textualData } = {} } = placeholderContent;

  // --- Offer machine (shared by filtering and fetching) ---
  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);
  const offer = offerState?.context?.offer;

  // --- Offer filtering ---
  const { offers, allOffers, availableOffers, showProductToggle, productFilter, setProductFilter, handleValueChange } =
    useOfferFiltering({
      getOffersConfig,
      transformOffers,
      showProductToggleOverride,
      onProductFilterChange,
      flowContext,
      offer,
    });

  // --- Form ---
  const form = useOfferForm({
    businessUnit,
    label,
    flowContext,
    offer,
    allOffers,
    availableOffers,
    validationMessage,
    autoReselectOnOffersChange,
    buildSubmitPayload,
  });

  // --- Offer fetching & state machine sync ---
  const fetching = useOfferFetching({
    businessUnit,
    label,
    flowContext,
    discountCode: flowContext.discountCode,
    offers,
    offer,
    offerState,
    sendToOfferMachine,
    textualData,
    isPreSelectionEnabled,
    machineConfig: {
      eventType: offerMachineEventType,
      payload: offerMachinePayload,
    },
    mountConfig: {
      onMount,
      sendToFlowMachine,
    },
    formActions: {
      setValue: form.setValue,
      setProductFilter,
    },
  });

  // --- Selection tracking ---
  useOfferTracking({
    flowContext,
    offer,
    selectedDiscountCode: form.selectedDiscountCode,
    discountCode: flowContext.discountCode,
  });

  // --- Layout & responsive ---
  const layout = useOfferLayout({
    availableOffers,
    offers,
    textualData,
    productFilter,
    scrollBehavior,
    scrollBlock,
  });

  // --- Consumption overview ---
  const consumption = useConsumptionOverview({
    flowContext,
    textualData,
    sendToFlowMachine,
  });

  const promotedOffer = useMemo(
    () =>
      getPromotedOffer({
        offer,
        offers,
        allOffers,
        hasDoubleMeter: flowContext.hasDoubleMeter,
        customerId: flowContext.customerId,
      }),
    [offer, offers, allOffers, flowContext.hasDoubleMeter, flowContext.customerId],
  );

  return {
    // DC
    businessUnit,
    label,

    // Flow context
    flowContext,
    sendToFlowMachine,
    updateContextAndGoToNextStep: form.updateContextAndGoToNextStep,

    // Offers
    offers,
    allOffers,
    availableOffers,
    offer,
    promotedOffer,
    showProductToggle,

    // Textual data (Sitecore)
    textualData,
    placeholderContent,

    // Form
    handleSubmit: form.handleSubmit,
    control: form.control,
    getValues: form.getValues,
    setValue: form.setValue,
    errors: form.errors,
    isSubmitSuccessful: form.isSubmitSuccessful,
    selectedDiscountCode: form.selectedDiscountCode,
    selectedDiscountCodeIndex: form.selectedDiscountCodeIndex,
    onSuccessfulSubmit: form.onSuccessfulSubmit,

    // UI state
    isLoading: fetching.isLoading,
    isSuccessful: fetching.isSuccessful,
    isFailed: fetching.isFailed,
    isNotFound: fetching.isNotFound,
    isNotAvailable: fetching.isNotAvailable,
    isNotAuthenticated: fetching.isNotAuthenticated,
    isPriceDetailsOpen: layout.isPriceDetailsOpen,
    setIsPriceDetailsOpen: layout.setIsPriceDetailsOpen,
    shouldShowMoreInfoDialog: fetching.shouldShowMoreInfoDialog,
    shouldShowCarousel: layout.shouldShowCarousel,
    shouldShowCarouselIndicators: layout.shouldShowCarouselIndicators,
    shouldAlignCarouselItems: layout.shouldAlignCarouselItems,
    shouldAttachCardRef: layout.shouldAttachCardRef,
    productFilter,
    cardRef: layout.cardRef,

    // Media queries
    isLargeBreakpoint: layout.isLargeBreakpoint,
    isMediumBreakpoint: layout.isMediumBreakpoint,
    isExtraLargeBreakpoint: layout.isExtraLargeBreakpoint,

    // Layout
    filteredOptions: layout.filteredOptions,
    hasSubtitleLayout: layout.hasSubtitleLayout,
    gridLayout: layout.gridLayout,
    buttonLayout: layout.buttonLayout,

    // Event handlers
    handleValueChange,
    onRadioValueChange,

    // Consumption overview
    ...consumption,

    // Context fields exposed for OfferCard
    energyType: flowContext.energyType,
    isSME: flowContext.isSME,
  };
}
